/**
 * One-line preview of a conversation's last message, shared by the message-agent
 * inbox and the unified inbox.
 *
 * The message type is read before the text: a caption-less media message has no
 * text at all, and rendering "No messages yet" on a conversation that just
 * received a photo is how a live thread looks abandoned.
 */

import { buildConversationPreview } from '@/lib/agents/messaging/conversation-preview'

/** Shared with the message-agent inbox row, which builds its preview from route data. */
export const MESSAGE_TYPE_LABELS: Record<string, string> = {
  image: 'Photo',
  video: 'Video',
  audio: 'Voice message',
  reaction: 'Reaction',
  story_reply: 'Story reply',
  file: 'File',
}

/** Non-text messages have no text to show; the type IS the preview. */
export function mediaMessageLabel(messageType: string | null): string | null {
  if (!messageType || messageType === "text") return null
  return MESSAGE_TYPE_LABELS[messageType] ?? messageType
}

export interface MessagePreviewSource {
  contentText: string | null
  messageType: string | null
  direction: 'inbound' | 'outbound' | null
  channelType: string
}

/** Null when the conversation has no message to preview — callers render their own empty state. */
export function resolveMessagePreview(message: MessagePreviewSource | null): string | null {
  if (!message) return null

  const mediaLabel = mediaMessageLabel(message.messageType)
  if (mediaLabel) return mediaLabel

  return buildConversationPreview(
    { content_text: message.contentText, direction: message.direction ?? 'inbound' },
    message.channelType,
  )
}
